import type { CSSProperties, ReactNode } from "react"

import { cx } from "../lib/cx"
import styles from "./skeleton.module.css"

/**
 * Каркас загрузки.
 *
 * Анимация и цвет плашки живут в base.css (.plnr-skeleton) — там же, откуда их берёт
 * состояние «проверяем доступ». Это сделано нарочно: по ТЗ загрузка и проверка прав
 * обязаны выглядеть ОДИНАКОВО, а два похожих набора стилей расходятся на первой же
 * правке, и тогда человек с полными правами на долю секунды увидит чужой экран.
 *
 * Каркас повторяет ФОРМУ будущего содержимого, а не показывает крутилку: администратор
 * узнаёт свой журнал раньше, чем данные доехали, и не начинает искать его заново.
 */

function plate(width: number | string, height: number, radius: number): CSSProperties {
  return {
    width: typeof width === "number" ? `${width}px` : width,
    height: `${height}px`,
    borderRadius: `${radius}px`,
  }
}

export function Skeleton(props: {
  readonly width: number | string
  readonly height: number
  readonly radius?: number
  readonly className?: string | undefined
}): ReactNode {
  return (
    <div
      className={cx("plnr-skeleton", props.className)}
      style={plate(props.width, props.height, props.radius ?? 4)}
      aria-hidden="true"
    />
  )
}

export function SkeletonCircle(props: { readonly size: number; readonly className?: string | undefined }): ReactNode {
  return (
    <div
      className={cx("plnr-skeleton", styles.circle, props.className)}
      style={{ width: `${props.size}px`, height: `${props.size}px` }}
      aria-hidden="true"
    />
  )
}

/** Несколько строк разной длины: так плашка читается как текст, а не как плита. */
export function SkeletonText(props: {
  readonly widths: readonly (number | string)[]
  readonly height?: number
  readonly className?: string | undefined
}): ReactNode {
  const height = props.height ?? 11
  return (
    <div className={cx(styles.lines, props.className)} aria-hidden="true">
      {props.widths.map((width, index) => (
        <div
          key={`${String(width)}-${String(index)}`}
          className="plnr-skeleton"
          style={plate(width, height, 3)}
        />
      ))}
    </div>
  )
}

/**
 * Область, которая грузится.
 *
 * aria-busy и одна строка словами — иначе человек, слушающий экран, получает тишину:
 * плашки помечены aria-hidden, и рассказывать ему нечего. Строка не мигает при каждой
 * перерисовке, потому что живёт в области с aria-live="polite" и меняется только
 * вместе с самим состоянием.
 */
export function SkeletonBlock(props: {
  readonly children: ReactNode
  /** Что именно грузится: «Загружаем день журнала». */
  readonly label: string
  readonly className?: string | undefined
}): ReactNode {
  return (
    <div className={cx(styles.block, props.className)} aria-busy="true" aria-live="polite">
      <span className="plnr-visually-hidden">{props.label}</span>
      {props.children}
    </div>
  )
}
